import { FC, useEffect } from 'react';

import { useTranslations } from '@lang';
import { useFormContext, useWatch } from 'react-hook-form';
import { Container } from 'styled-bootstrap-grid';

import { Button, InfoBox, StickyBarContent } from '@common/components';
import Portal from '@common/components/Portal';

import { SpaEntityFormValues } from '@common/components/SpaEntity/SpaEntityForm.schema';
import { EntityTypes } from '@common-types/Entities';
import { resolveSpaGuestBounds } from '@features/spa/utils/resolveSpaGuestBounds';
import { AddGuestsForm } from './AddGuestsForm';
import { GuestDetailsProps } from './GuestDetails.types';

export const GuestDetails: FC<GuestDetailsProps> = ({
  spaEntity,
  typeName = 'Spa Treatment',
  entityType,
  stickyBarId,
  onSubmit,
  existingGuests = [],
  handleInvalidConfig,
  guest = null,
  showGenderNeutral,
  isItemFetching,
  requestedGuests,
}) => {
  const { t } = useTranslations();
  const { handleSubmit, formState, setValue } = useFormContext<SpaEntityFormValues>();
  const selectedArrivalTime = useWatch({ name: 'arrivalTime' });
  const selectedGuests = useWatch({ name: 'guests' });

  useEffect(() => {
    if (!selectedGuests?.length) {
      handleInvalidConfig();
    }
  }, [selectedGuests, handleInvalidConfig]);

  const getCalculatedPrice = () => ({
    ...selectedArrivalTime.price,
    amount: selectedArrivalTime.price.amount * (selectedGuests?.length ?? 1),
  });

  const getCalculatedDiscount = () =>
    selectedArrivalTime.discount
      ? {
          discountedPrice: {
            ...selectedArrivalTime.discount?.discountedPrice,
            amount:
              selectedArrivalTime.discount?.discountedPrice.amount * (selectedGuests?.length ?? 1),
          },
        }
      : undefined;

  const { availability, extras, minimumGuests, selectionGroups, maximumGuests } = spaEntity || {};
  // The selected arrival time carries the live availability for that specific slot. Prefer it over
  // the entity's availability list, which is only fetched for the initial date and won't contain
  // slots the guest picks on other days of the week.
  const timeslotMaxGuests =
    selectedArrivalTime && availability
      ? availability?.find(
          (arrivalTime) => selectedArrivalTime.startDateTime === arrivalTime.startDateTime
        )
      : null;

  const { min: minGuests, max: maxGuests } = resolveSpaGuestBounds(
    { minimumGuests, maximumGuests },
    {
      slotCapacity: selectedArrivalTime?.numberAvailable ?? timeslotMaxGuests?.numberAvailable,
      requestedGuests,
    }
  );
  const isExtrasAvailable = extras?.length || selectionGroups?.length;

  const Gender = { M: 'male', F: 'female' };
  const defaultGender = spaEntity?.genderSpecific ? Gender[spaEntity.genderSpecific] || '' : '';

  useEffect(() => {
    if (!selectedArrivalTime) return;
    if ((selectedGuests?.length ?? 0) > maxGuests) {
      setValue('guests', selectedGuests.slice(0, maxGuests));
    }
  }, [selectedArrivalTime, maxGuests, selectedGuests, setValue]);

  return selectedArrivalTime ? (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Container fluid>
        <AddGuestsForm
          existingGuests={existingGuests}
          guest={guest as any}
          minimumGuests={minGuests}
          maximumGuests={maxGuests}
          spaEntity={spaEntity}
          showGenderNeutral={showGenderNeutral}
          typeName={typeName}
          isItemFetching={isItemFetching}
          defaultGender={defaultGender}
        />
        {entityType === EntityTypes.TREATMENT && <InfoBox>{t('spa.timeslotsWarning')}</InfoBox>}
      </Container>
      <Portal selector={`#${stickyBarId}`}>
        <StickyBarContent
          name={spaEntity.name}
          displayedPrice={getCalculatedPrice() as any}
          discountedPrice={getCalculatedDiscount()}
          button={
            <Button
              isPrimary
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={formState.isSubmitting}
            >
              {!!isExtrasAvailable && t('treatment.customizeTreatmentsCTA')}
              {!isExtrasAvailable && t('treatment.addToBasketCTA')}
            </Button>
          }
        />
      </Portal>
    </form>
  ) : null;
};
